// src/services/emailService.js
// Serviço de envio de e-mails via EmailJS
// Documentação: https://www.emailjs.com/docs/

import emailjs from '@emailjs/browser';
import { isValidEmail, sanitizeString } from 'shared/utils/security';

const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// IDs dos templates criados no painel EmailJS
const TEMPLATES = {
  appointmentConfirmation: process.env.REACT_APP_EMAILJS_TEMPLATE_CONFIRMATION,
  appointmentReminder:     process.env.REACT_APP_EMAILJS_TEMPLATE_REMINDER,
  appointmentCancelled:    process.env.REACT_APP_EMAILJS_TEMPLATE_CANCELLED,
  appointmentRescheduled:  process.env.REACT_APP_EMAILJS_TEMPLATE_RESCHEDULED,
};

const isConfigured = () => SERVICE_ID && PUBLIC_KEY;

// Formata data para exibição: "2026-05-21" → "21/05/2026"
function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

// Formata hora para exibição: "09:00" → "09h00"
function formatTime(timeStr) {
  if (!timeStr) return '';
  return timeStr.replace(':', 'h');
}

// Envia via EmailJS com variáveis padronizadas para template único
function send(templateKey, params) {
  if (!isConfigured()) {
    console.warn('[EmailJS] SERVICE_ID ou PUBLIC_KEY não configurados no .env');
    return Promise.resolve();
  }
  if (!TEMPLATES[templateKey]) {
    console.warn(`[EmailJS] Template "${templateKey}" não configurado no .env`);
    return Promise.resolve();
  }
  if (!isValidEmail(params.to_email)) {
    console.warn('[EmailJS] E-mail de destino inválido — envio bloqueado:', params.to_email);
    return Promise.resolve();
  }
  console.log(`[EmailJS] Enviando "${templateKey}" para ${params.to_email}...`);
  return emailjs
    .send(SERVICE_ID, TEMPLATES[templateKey], params, PUBLIC_KEY)
    .then(res => {
      console.log(`[EmailJS] ✅ Enviado com sucesso! Status: ${res.status}`);
      return res;
    })
    .catch(err => {
      console.error('[EmailJS] ❌ Erro ao enviar:', err?.text || err?.message || err);
      throw err;
    });
}

// Monta payload sanitizado no formato das variáveis do template EmailJS:
// {{to_email}}, {{name}}, {{time}}, {{message}}
function buildPayload(clientName, clientEmail, title, body, dateStr, timeStr) {
  return {
    to_email: sanitizeString(clientEmail).trim(),
    name:     sanitizeString(clientName),
    time:     timeStr ? `${formatDate(dateStr)} às ${formatTime(timeStr)}` : formatDate(dateStr),
    message:  `${sanitizeString(title)}\n\n${sanitizeString(body)}`,
  };
}

export async function sendAppointmentConfirmation({ clientName, clientEmail, professionalName, procedureName, date, time, price }) {
  const priceStr = price ? ` — R$ ${Number(price).toFixed(2).replace('.', ',')}` : '';
  const body = `Procedimento: ${procedureName}${priceStr}\nProfissional: ${professionalName}\nData: ${formatDate(date)} às ${formatTime(time)}`;
  return send('appointmentConfirmation',
    buildPayload(clientName, clientEmail, '✅ Agendamento confirmado!', body, date, time)
  );
}

export async function sendAppointmentCancelled({ clientName, clientEmail, professionalName, procedureName, date, time }) {
  const body = `Procedimento: ${procedureName}\nProfissional: ${professionalName}\nData: ${formatDate(date)} às ${formatTime(time)}\n\nO horário ficou disponível para outro cliente.`;
  return send('appointmentCancelled',
    buildPayload(clientName, clientEmail, '❌ Agendamento cancelado', body, date, time)
  );
}

export async function sendAppointmentReminder({ clientName, clientEmail, professionalName, procedureName, date, time }) {
  const body = `Você tem um agendamento amanhã!\n\nProcedimento: ${procedureName}\nProfissional: ${professionalName}\nData: ${formatDate(date)} às ${formatTime(time)}`;
  return send('appointmentReminder',
    buildPayload(clientName, clientEmail, '🔔 Lembrete de agendamento', body, date, time)
  );
}

export async function sendAppointmentRescheduled({ clientName, clientEmail, professionalName, procedureName, oldDate, oldTime, newDate, newTime }) {
  const body = `Procedimento: ${procedureName}\nProfissional: ${professionalName}\n\nHorário anterior: ${formatDate(oldDate)} às ${formatTime(oldTime)}\nNovo horário: ${formatDate(newDate)} às ${formatTime(newTime)}`;
  return send('appointmentRescheduled',
    buildPayload(clientName, clientEmail, '🔄 Agendamento reagendado', body, newDate, newTime)
  );
}
